import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CountryCode } from 'libphonenumber-js';
import { BehaviorSubject, filter, take } from 'rxjs';
import { PillComponent } from '../../../../shared/components/pill/pill.component';
import { UserContextProvider } from '../../../../infrastructure/context/user-context-provider';
import { DataTableColumn } from '../../../../shared/components/data-table/DataTableColumn.model';
import { OrganizationRow } from '../../models/Organization';
import { OrganizationService } from '../../services/organizations.service';
import { Logger } from '../../../../infrastructure/logging/logger';
import { ActivateEmbeddedOrgDialogComponent } from './activate-embedded-org-dialog/activate-embedded-org-dialog.component';
import { ParnterOrganizationStatusColor, PartnerAccountStatusEnum, PartnerOrganization } from '../../models/PartnerOrganization';
import { formatPhoneNumber } from '../../../../utilities/PhoneUtility';
import { NotificationService } from '../../../../utilities/PortalNotificationUtility';
import { dateFormat } from '../../../../utilities/DateUtility';
import { DataTableFilter } from '../../../../shared/components/data-table/filters/components/DataTableFilter';
import { PartnerOrgsFilterStatusComponent } from './components/partner-orgs-filter-status/partner-orgs-filter-status.component';
import { DataTableFilterComponent } from '../../../../shared/components/data-table/filters/components/data-table-filter/data-table-filter.component';
import { PartnerOrgMessageService } from '../../services/partner-org-message.service';
import { PartnerOrgFilterStateService } from '../../services/partner-org-filter-state.service';
import { DataTableFilterPillComponent, DataTableFilterPillState } from '../../../../shared/components/data-table/filters/components/data-table-filter-pill/data-table-filter-pill.component';
import { ActiveEventMessage, DataTableFilterMessageService } from '../../../../shared/components/data-table/filters/components/data-table-filter-message-service';

@Component({
  selector: 'app-embedded-org-table',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatTabsModule,
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    FormsModule,
    MatIconModule,
    PillComponent,
    MatProgressSpinner,
    DataTableFilterComponent,
    DataTableFilterPillComponent
  ],
  templateUrl: './embedded-org-table.component.html',
  styleUrl: './embedded-org-table.component.scss'
})
export class EmbeddedOrgTableComponent {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort;

  displayedColumns: DataTableColumn[] = [
    { column: 'referenceId', display: 'Reference ID' },
    { column: 'organizationId', display: 'Organization ID' },
    { column: 'name', display: 'Organization Name' },
    { column: 'partnerId', display: 'Partner ID' },
    { column: 'contactName', display: 'Contact Name' },
    { column: 'email', display: 'Email' },
    { column: 'phoneNumber', display: 'Phone' },
    { column: 'coverageStartDate', display: 'Activation/Deactivation Date' },
    { column: 'pillStatus', display: 'Status' },
    { column: 'activateEmbeddedOrgButtons', display: ''},
    { column: 'deactivateEmbeddedOrgButtons', display: ''}
  ]

  filterDefinitions: DataTableFilter[] = [
    { id: 'partner-organization-filter-status', name: 'Partner Organization Status', shortName: 'Status', component: PartnerOrgsFilterStatusComponent }
  ];
  filterPills = new Map<string, DataTableFilterPillState>();

  loading = new BehaviorSubject<boolean>(false);
  displayedColumnNames: string[] = [];
  searchKeyword: string = '';
  tableLength = 0;
  pageSize = 10;
  pageNumber: number = 1;

  partnerOrganizationsDataSource = new MatTableDataSource<OrganizationRow>();
  mappedPartnerOrganizations: any[] = [];
  partnerOrganizationsTableLength = 0;
  partnerOrganizationsPageSize = 10;
  organizationConnector: any;

  allowActivation: boolean = false;
  allowDeactivation: boolean = false;

  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  statusFilterArray = [ 'Active', 'Pending Active', 'Onboarding In Progress', 'Pending Inactive', 'Inactive', 'Terminated', 'Deleted', 'Staging' ];
  activeFilter: boolean = false;
  activeCustomFilter: boolean = false;
  pendingActiveFilter: boolean = false;
  onboardingInProgressFilter: boolean = false;
  onboardingTimedOutFilter: boolean = false;
  pendingInactiveFilter: boolean = false;
  inactiveFilter: boolean = false;
  rejected: boolean = false;
  terminated: boolean = false;
  deleted: boolean = false;
  staging: boolean = false;

  constructor(
    private dialog: MatDialog,
    private organizationService: OrganizationService,
    private notificationService: NotificationService,
    private partnerOrgMessageService: PartnerOrgMessageService,
    private partnerOrgFilterStateService: PartnerOrgFilterStateService,
    private filterMessageService: DataTableFilterMessageService,
    private logger: Logger,
    private userContextProvider: UserContextProvider,
    private matSnackbar: MatSnackBar,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(_ => {
      this.updateFilterPillTextAfterFilterEvent();
      this.setupFilterEventHandler();
      this.initTableColumns();
      this.loadPartnerOrganizations();

      const storedItemsPerPage = localStorage.getItem('partnerOrganizationItemsPerPage');
      if (storedItemsPerPage) {
        this.initialPageSize.next(parseInt(storedItemsPerPage, 10));
      } else {
        this.initialPageSize.next(10);
      }
    });
  }

  ngAfterViewInit() {
    this.partnerOrganizationsDataSource.paginator = this.paginator;
    this.partnerOrganizationsDataSource.sort = this.sort;

    this.initialPageSize?.subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.searchKeyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  private setupFilterEventHandler() {
    this.partnerOrgMessageService.applyFilterEvent.subscribe(_ => {
      this.updateAllStatusFilters();
    });
  }

  private updateAllStatusFilters() {
    let statusFilterArray: string[] = [];

    if (this.partnerOrgFilterStateService.active) {
      statusFilterArray.push('ACTIVE');
    }
    if (this.partnerOrgFilterStateService.pendingActive) {
      statusFilterArray.push('PENDING_ACTIVE');
    }
    if (this.partnerOrgFilterStateService.onboardingInProgress) {
      statusFilterArray.push('ONBOARDING_IN_PROGRESS');
    }
    if (this.partnerOrgFilterStateService.pendingInactive) {
      statusFilterArray.push('PENDING_INACTIVE');
    }
    if (this.partnerOrgFilterStateService.inactive) {
      statusFilterArray.push('INACTIVE');
    }
    if (this.partnerOrgFilterStateService.terminated) {
      statusFilterArray.push('TERMINATED');
    }
    if (this.partnerOrgFilterStateService.deleted) {
      statusFilterArray.push('DELETED');
    }
    if (this.partnerOrgFilterStateService.staging) {
      statusFilterArray.push('STAGING');
    }

    this.updateStatusFilterAfterFilterApplied(statusFilterArray);
  }

  updateStatusFilterAfterFilterApplied(statusFilterArray: string[]) {
    if (statusFilterArray.length === 10) {
      this.partnerOrganizationsDataSource.data = this.mappedPartnerOrganizations;
    } else {
      const filteredPartnerOrganizations = this.mappedPartnerOrganizations.filter(partnerOrg => statusFilterArray.find((statusText: string) => partnerOrg.pillStatus === statusText));
      this.partnerOrganizationsDataSource.data = filteredPartnerOrganizations;
    }
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }
    this.displayedColumnNames = this.displayedColumns.map(c => c.column);
  }

  async loadPartnerOrganizations() {
    try {
      this.loading.next(true);
      this.partnerOrganizationsDataSource.data = [];

      const partnerOrganizations = await this.organizationService.getPartnerOrganizations();
      this.mappedPartnerOrganizations = (await Promise.allSettled(partnerOrganizations.map(o => this.buildPartnerOrganizationRow(o)))).filter(r => r.status === 'fulfilled').map(r => r.value);
      this.updatePartnerOrganizationsTableDataSource(this.mappedPartnerOrganizations);
    } catch (e) {
      this.logger.handleError(e, 'An error occurred while retrieving Partner Organizations response', 'Error while retrieving Partner Organizations response');
    } finally {
      this.loading.next(false);
    }
  }

  async buildPartnerOrganizationRow(partnerOrganization: PartnerOrganization) {
    let coverage_start_date: any;
    let coverage_end_date: any;

    if (
      partnerOrganization.status === PartnerAccountStatusEnum.ACTIVE ||
      partnerOrganization.status === PartnerAccountStatusEnum.PENDING_ACTIVE
    ) {
      if (partnerOrganization.coverageStartDate) {
        coverage_start_date = partnerOrganization.coverageStartDate;
      } else {
        const organization_detail = await this.organizationService.getV2OrganizationDetail(partnerOrganization.organizationId);
        coverage_start_date = organization_detail.startDate;
      }
    }

    if (
      partnerOrganization.status === PartnerAccountStatusEnum.PENDING_INACTIVE ||
      partnerOrganization.status === PartnerAccountStatusEnum.INACTIVE ||
      partnerOrganization.status === PartnerAccountStatusEnum.TERMINATED
    ) {
      if (partnerOrganization.coverageEndDate) {
        coverage_start_date = partnerOrganization.coverageEndDate;
        coverage_end_date = partnerOrganization.coverageEndDate;
      }
    }

    return {
      referenceId: partnerOrganization.referenceId,
      organizationId: partnerOrganization.organizationId ? partnerOrganization.organizationId : '',
      name: partnerOrganization.onboardingRequest.companyName,
      contactName: `${partnerOrganization.onboardingRequest.firstName} ${partnerOrganization.onboardingRequest.lastName}`,
      email: partnerOrganization.onboardingRequest.email,
      phoneNumber: formatPhoneNumber(partnerOrganization.onboardingRequest.phone, this.userContextProvider.countryCode as CountryCode),
      pillStatus: partnerOrganization.status,
      coverageStartDate: dateFormat(coverage_start_date, this.userContextProvider.region),
      partnerId: partnerOrganization.partnerId ? partnerOrganization.partnerId.charAt(0).toUpperCase() + partnerOrganization.partnerId.slice(1) : '',
      lodgifyId: partnerOrganization.referenceId,
      phone: partnerOrganization.onboardingRequest.phone,
      website: partnerOrganization.onboardingRequest.websiteUrl,
      statusReason: partnerOrganization.statusReason,
      addressLine1: partnerOrganization.onboardingRequest.addressLine1,
      addressLine2: partnerOrganization.onboardingRequest.addressLine2 ? partnerOrganization.onboardingRequest.addressLine2 : '',
      city: partnerOrganization.onboardingRequest.city,
      state: partnerOrganization.onboardingRequest.stateCode,
      postalCode: partnerOrganization.onboardingRequest.postalCode,
      country: partnerOrganization.onboardingRequest.countryCode
    }
  }

  updatePartnerOrganizationsTableDataSource(partnerOrganizations: OrganizationRow[]) {
    this.partnerOrganizationsDataSource.data = partnerOrganizations;
    this.partnerOrganizationsTableLength = partnerOrganizations.length;
  }

  onActivateEmbeddedOrganizationButtonClick(row: PartnerOrganization) {
    this.checkPropertyPoliciesByOrgId(row);
  }

  async checkPropertyPoliciesByOrgId(partner_org: PartnerOrganization) {
    const organizationRow = await this.organizationService.getV2OrganizationDetail(partner_org.organizationId);
    partner_org.requestStartDate = new Date(organizationRow.startDate);
    this.organizationConnector = await this.organizationService.getOrganizationConnectorById(partner_org.organizationId);
    if (this.organizationConnector.lastSuccess) {
      this.openActivateEmbeddedOrganizationDialog(partner_org);
    } else {
      this.notificationService.showNotification({ message: `Organization ID: ${partner_org.organizationId} cannot be activated at this time. Organization Connector: 'lastSuccess response = null'.` })
    }
  }

  openActivateEmbeddedOrganizationDialog(partner_org: PartnerOrganization) {
    const activateEmbeddedOrgDialogRef = this.dialog.open(ActivateEmbeddedOrgDialogComponent, {
      width: '55%',
      maxHeight: '850px',
      disableClose: false,
      data: partner_org
    });
    activateEmbeddedOrgDialogRef.afterClosed().subscribe(async data => {
      if (data) {
        try {
          let partnerOrgUpdatePayload = {
            statusChangeDate: data.activation_date.toISOString().slice(0, 10),
            status: "PENDING_ACTIVE"
          }
          const partnerOrganizationStatusResponse = await this.organizationService.updatePartnerOrganization(partner_org.organizationId, partnerOrgUpdatePayload);

          if (partnerOrganizationStatusResponse) {
            try {
              let startDatePayload = {
                startDate: data.activation_date.toISOString().slice(0, 10),
                activeDates: [{
                  type: "ARRIVAL",
                  minimumDate: data.activation_date.toISOString().slice(0, 10)
                }]
              }
              const orgStartDateResponse = await this.organizationService.updateV2OrganizationStatusDate(partner_org.organizationId, startDatePayload);

              if (orgStartDateResponse) {
                const payload = {
                  partnerId: partner_org.partnerId.toLowerCase(),
                  referenceId: partner_org.referenceId,
                  statusText: "PENDING_ACTIVE"
                }
                this.organizationService.updatePartnerOrgOnboardingStatus(payload).then(_ => {
                  this.loadPartnerOrganizations();
                });
              }
            } catch(e) {
              this.logger.handleError(e, `An error occurred while updating Organization ID: ${data.organization_id} start date`, `An error occurred while updating Organization ID: ${data.organization_id} start date`);
            }
          }
        } catch(e) {
          this.logger.handleError(e, `An error occurred while updating Partner Organization ID: ${data.organization_id} status`, `An error occurred while updating Partner Organization ID: ${data.organization_id} status`);
        } finally {
          this.loadPartnerOrganizations();
        }
      }
    });
  }

  applySearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.partnerOrganizationsDataSource.filter = filterValue.trim().toLowerCase();

    if (this.partnerOrganizationsDataSource.paginator) {
      this.partnerOrganizationsDataSource.paginator.firstPage();
    }
  }

  clearSearch() {
    this.searchKeyword = '';
    this.partnerOrganizationsDataSource = new MatTableDataSource(this.mappedPartnerOrganizations);
    this.ngAfterViewInit();
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  onFilterPillCloseEvent(filterId: string) {
    this.filterMessageService.onClearClicked(filterId);
  }

  updateFilterPillTextAfterFilterEvent() {
    this.filterMessageService.activeEvent.subscribe((event: ActiveEventMessage) => {
      if (!this.filterDefinitions) {
        return;
      }

      this.resetPaginator(1, true);
      if (event.active) {
        let pillText = this.filterDefinitions.find(definition => definition.id == event.filterId)?.shortName || '';
        if (event.value) {
          pillText = pillText + " : " + event.value;
        }
        this.filterPills.set(event.filterId, { filterId: event.filterId, text: pillText });
      } else {
        this.filterPills.delete(event.filterId);
      }
      this.cdr.detectChanges();
    });
  }

  displayInvalidPaginatorPageIndexError() {
    this.matSnackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  onRowClick(row: any): void {
    this.router.navigate([`/organizations/detail/${row['organizationId']}`], { state: { row } });
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    localStorage.setItem('partnerOrganizationItemsPerPage', event.pageSize.toString());
  }

  getPartnerOrganizationPillStatusColor = (status?: string): string => {
    switch (status) {
      case "ACTIVE":
        return ParnterOrganizationStatusColor.ACTIVE;
      case "ACTIVE_CUSTOM":
        return ParnterOrganizationStatusColor.ACTIVE_CUSTOM;
      case "ONBOARDING_IN_PROGRESS":
        return ParnterOrganizationStatusColor.ONBOARDING_IN_PROGRESS;
      case "ONBOARDING_TIMED_OUT":
        return ParnterOrganizationStatusColor.ONBOARDING_TIMED_OUT;
      case "PENDING_ACTIVE":
        return ParnterOrganizationStatusColor.PENDING_ACTIVE;
      case "REJECTED":
        return ParnterOrganizationStatusColor.REJECTED;
      case "INACTIVE":
        return ParnterOrganizationStatusColor.INACTIVE;
      case "TERMINATED":
        return ParnterOrganizationStatusColor.TERMINATED;
      case "DELETED":
        return ParnterOrganizationStatusColor.REJECTED;
      case "STAGING":
        return ParnterOrganizationStatusColor.STAGING;
      default:
        return ParnterOrganizationStatusColor.PENDING_ACTIVE;
    }
  }

  getFormattedPartnerOrganizationStatus = (status?: string): string => {
    switch (status) {
      case "ACTIVE":
        return "ACTIVE";
      case "ACTIVE_CUSTOM":
        return "ACTIVE CUSTOM";
      case "ONBOARDING_IN_PROGRESS":
        return "ONBOARDING IN PROGRESS";
      case "ONBOARDING_TIMED_OUT":
        return "ONBOARDING TIMED OUT";
      case "PENDING_ACTIVE":
        return "PENDING ACTIVE";
      case "REJECTED":
        return "REJECTED";
      case "INACTIVE":
        return "INACTIVE";
      case "TERMINATED":
        return "TERMINATED";
      case "DELETED":
        return "DELETED";
      case "STAGING":
        return "STAGING";
      default:
        return "NEW";
    }
  }
}
